import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Trash2, Pencil, X, Lock, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { apiFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [customTypes, setCustomTypes] = useState([]);
  const [newCustomType, setNewCustomType] = useState('');
  const [showCustomTypeInput, setShowCustomTypeInput] = useState(false);
  const [accountLimits, setAccountLimits] = useState(null);

  const defaultTypes = [
    'Gestion client', 'Organisation personnelle', 'Suivi administratif',
    'Projet freelance', 'Mission client', 'Événement',
    'Formation', 'Recrutement', 'Projet immobilier',
    'Lancement d\'activité', 'Suivi commercial'
  ];

  const [formData, setFormData] = useState({
    client_name: '', project_type: '', project_manager: '',
    start_date: '', estimated_delivery: '', description: '', budget: '', team_members: []
  });

  const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
  const userId = user?.id;

  const fetchProjects = useCallback(async () => {
    try {
      const res = await apiFetch('/api/projects');
      setProjects(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/team');
      setTeamMembers(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchCustomTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/project-types`, { headers: { 'X-User-Id': userId } });
      if (res.ok) setCustomTypes(await res.json());
    } catch (e) { console.error(e); }
  }, [userId]);

  const fetchAccountLimits = useCallback(async () => {
    try {
      const res = await apiFetch('/api/account/limits');
      if (res.ok) setAccountLimits(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchProjects(); fetchTeamMembers(); fetchCustomTypes(); fetchAccountLimits(); }, [fetchProjects, fetchTeamMembers, fetchCustomTypes, fetchAccountLimits]);

  useEffect(() => {
    let filtered = [...projects];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => p.client_name?.toLowerCase().includes(q) || p.project_type?.toLowerCase().includes(q));
    }
    if (filterStatus !== 'all') filtered = filtered.filter(p => p.status === filterStatus);
    setFilteredProjects(filtered);
  }, [projects, searchQuery, filterStatus]);

  const allTypes = [...defaultTypes, ...customTypes.filter(t => !defaultTypes.includes(t))];

  const resetForm = () => {
    setFormData({ client_name: '', project_type: '', project_manager: '', start_date: '', estimated_delivery: '', description: '', budget: '', team_members: [] });
    setShowCustomTypeInput(false);
    setNewCustomType('');
  };

  const handleAddProject = async () => {
    try {
      const body = { ...formData, budget: formData.budget ? parseFloat(formData.budget) : null };
      const res = await apiFetch('/api/projects', { method: 'POST', body: JSON.stringify(body) });
      if (res.ok) { await fetchProjects(); await fetchAccountLimits(); setIsAddDialogOpen(false); resetForm(); }
    } catch (e) { console.error(e); }
  };

  const handleEditProject = (e, project) => {
    e.stopPropagation();
    setEditingProject(project);
    setFormData({
      client_name: project.client_name || '', project_type: project.project_type || '',
      project_manager: project.project_manager || '', start_date: project.start_date || '',
      estimated_delivery: project.estimated_delivery || '', description: project.description || '',
      budget: project.budget || '', team_members: project.team_members || []
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const body = { ...formData, budget: formData.budget ? parseFloat(formData.budget) : null };
      const res = await apiFetch(`/api/projects/${editingProject.id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (res.ok) { await fetchProjects(); setIsEditDialogOpen(false); setEditingProject(null); resetForm(); }
    } catch (e) { console.error(e); }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) return;
    try {
      const res = await apiFetch(`/api/projects/${projectId}`, { method: 'DELETE' });
      if (res.ok) await fetchProjects();
    } catch (e) { console.error(e); }
  };

  const handleAddCustomType = async () => {
    const name = newCustomType.trim();
    if (!name) return;
    try {
      await fetch(`${API}/api/project-types`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ name })
      });
      await fetchCustomTypes();
      setFormData({ ...formData, project_type: name });
      setShowCustomTypeInput(false);
      setNewCustomType('');
    } catch (e) { console.error(e); }
  };

  const toggleTeamMember = (name) => {
    const current = formData.team_members || [];
    setFormData({
      ...formData,
      team_members: current.includes(name) ? current.filter(n => n !== name) : [...current, name]
    });
  };

  const getHealthColor = (h) => {
    const map = { 'Excellent': 'text-green-600 bg-green-50', 'Bon': 'text-blue-600 bg-blue-50', 'À risque': 'text-orange-600 bg-orange-50', 'Critique': 'text-red-600 bg-red-50' };
    return map[h] || 'text-gray-600 bg-gray-50';
  };

  const getStatusColor = (s) => {
    const map = { 'En cours': 'bg-blue-100 text-blue-700', 'Terminé': 'bg-green-100 text-green-700', 'En attente': 'bg-orange-100 text-orange-700' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  const projectFormJSX = (
    <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Nom du client *</label>
          <Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} placeholder="Ex: Boulangerie Dupont" data-testid="project-client-name" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Type de projet *</label>
          {showCustomTypeInput ? (
            <div className="flex gap-2">
              <Input value={newCustomType} onChange={(e) => setNewCustomType(e.target.value)} placeholder="Nouveau type..." className="flex-1" autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleAddCustomType()} data-testid="project-custom-type-input" />
              <Button size="sm" onClick={handleAddCustomType} disabled={!newCustomType.trim()}>OK</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCustomTypeInput(false)}><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <Select value={formData.project_type} onValueChange={(v) => v === '__custom__' ? setShowCustomTypeInput(true) : setFormData({ ...formData, project_type: v })}>
              <SelectTrigger data-testid="project-type-select"><SelectValue placeholder="Sélectionner un type" /></SelectTrigger>
              <SelectContent>
                {allTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                <SelectItem value="__custom__">+ Ajouter un type personnalisé</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Chef de projet *</label>
        <Select value={formData.project_manager} onValueChange={(v) => setFormData({ ...formData, project_manager: v })}>
          <SelectTrigger data-testid="project-manager-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
          <SelectContent>
            {teamMembers.map(m => <SelectItem key={m.id} value={m.name}>{m.name} — {m.role}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Date de début *</label>
          <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} data-testid="project-start-date" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Date de livraison estimée *</label>
          <Input type="date" value={formData.estimated_delivery} onChange={(e) => setFormData({ ...formData, estimated_delivery: e.target.value })} data-testid="project-delivery-date" />
        </div>
      </div>

      {/* Collaborateurs */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Collaborateurs affectés</label>
        <div className="flex flex-wrap gap-2">
          {teamMembers.map(m => (
            <Badge key={m.id} variant={formData.team_members?.includes(m.name) ? 'default' : 'outline'}
              className="cursor-pointer select-none" onClick={() => toggleTeamMember(m.name)}>
              {m.name}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Description</label>
        <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Description du projet" rows={3} data-testid="project-description" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Budget (€)</label>
        <Input type="number" value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} placeholder="Ex: 5000" data-testid="project-budget" />
      </div>
    </div>
  );

  const canCreateProject = !accountLimits || accountLimits.can_create_project;
  const isFree = accountLimits?.account_type === 'free';

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Free account banner */}
      {isFree && !canCreateProject && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="free-limit-banner">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Limite du plan gratuit atteinte</p>
                <p className="text-sm text-amber-700">Votre compte gratuit vous permet de créer jusqu'à 5 projets. Passez au plan Pro pour créer des projets illimités.</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/pricing')} className="flex-shrink-0" data-testid="upgrade-btn-projects">
              Passer au Pro <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="page-title">Projets</h1>
          <p className="text-base text-muted-foreground mt-2">Gérez tous vos projets</p>
        </div>
        {canCreateProject ? (
          <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }} data-testid="add-project-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau projet
          </Button>
        ) : (
          <Button variant="outline" onClick={() => navigate('/pricing')} data-testid="add-project-btn-disabled" className="text-muted-foreground">
            <Lock className="w-4 h-4 mr-2" /> Nouveau projet
          </Button>
        )}
      </div>

      <Card className="p-4" data-testid="filters-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher un projet..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-input" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="En cours">En cours</SelectItem>
              <SelectItem value="Terminé">Terminé</SelectItem>
              <SelectItem value="En attente">En attente</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {filteredProjects.map((project, idx) => (
            <motion.div key={project.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }} onClick={() => navigate(`/project/${project.id}`)} className="cursor-pointer" data-testid={`project-card-${idx}`}>
              <Card className="p-6 hover:shadow-lg transition-shadow h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold truncate mb-1">{project.client_name}</h3>
                    <Badge variant="outline" className="text-xs">{project.project_type}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge className={getHealthColor(project.health_score)}>{project.health_score}</Badge>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-blue-600 hover:bg-blue-50"
                      onClick={(e) => handleEditProject(e, project)} data-testid={`edit-project-${idx}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                      onClick={(e) => handleDeleteProject(e, project.id)} data-testid={`delete-project-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    <p>Chef de projet: <span className="font-medium text-foreground">{project.project_manager}</span></p>
                    <p className="mt-1">Livraison: <span className="font-medium text-foreground">{project.estimated_delivery}</span></p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Progression</span>
                      <span className="font-medium">{project.progress}%</span>
                    </div>
                    <Progress value={project.progress} className="h-2" />
                  </div>
                  <div className="pt-3 border-t">
                    <Badge className={getStatusColor(project.status)}>{project.status}</Badge>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
          {filteredProjects.length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground">Aucun projet trouvé</div>
          )}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-project-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau projet</DialogTitle>
            <DialogDescription>Créer un nouveau projet client</DialogDescription>
          </DialogHeader>
          {projectFormJSX}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddProject} disabled={!formData.client_name || !formData.project_type || !formData.project_manager || !formData.start_date || !formData.estimated_delivery}>
              Créer le projet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle>Modifier le projet</DialogTitle>
            <DialogDescription>Mettre à jour les informations du projet</DialogDescription>
          </DialogHeader>
          {projectFormJSX}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveEdit} disabled={!formData.client_name || !formData.project_type || !formData.project_manager || !formData.start_date || !formData.estimated_delivery}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
